import React, { useState, useEffect } from 'react';
import { XCircle, Wallet, CreditCard, AlertTriangle, Loader2, X } from 'lucide-react';
import { PAYMENT_METHODS, isMoneyHeld } from '../../lib/paymentLabels';

const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toFixed(2)}`;

export default function CancelOrderModal({ order, onConfirm, onClose, isLoading }) {
  const [refundTo, setRefundTo] = useState('source');
  const [reason, setReason] = useState('');

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !isLoading) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLoading, onClose]);

  const isCOD        = order.paymentMethod === PAYMENT_METHODS.COD;
  // isMoneyHeld, not `paymentStatus === 'paid'`: an amended order sits at
  // 'partial_refund' while the revised total is still fully paid, and reading
  // only 'paid' dropped it into the unpaid arm below — telling a customer with a
  // refundable balance that cancellation was free, and hiding the refund-method
  // choice so the online leg silently defaulted to 'source' (BMF-159).
  const isPaid       = isMoneyHeld(order);
  const isWalletOnly = order.paymentMethod === PAYMENT_METHODS.WALLET;
  const isMixed      = order.paymentMethod === PAYMENT_METHODS.WALLET_RAZORPAY;
  const isRazorpay   = order.paymentMethod === PAYMENT_METHODS.RAZORPAY;

  // Show refund choice only for paid Razorpay/mixed orders
  const showRefundChoice = isPaid && (isRazorpay || isMixed);
  // Wallet-only paid orders: auto-refund message, no choice
  const showWalletAutoRefund = isPaid && isWalletOnly;

  const handleConfirm = () => {
    onConfirm(showRefundChoice ? refundTo : null, reason.trim() || null);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !isLoading) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Cancel Order</h2>
              <p className="text-sm text-gray-500 mt-0.5">#{order.orderNumber}</p>
            </div>
          </div>
          {!isLoading && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-5">

          {/* Order summary */}
          <div className="bg-gray-50 rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">Order Total</span>
            <span className="font-bold text-gray-900">{formatCurrency(order.total)}</span>
          </div>

          {/* ── Wallet-only auto-refund notice ── */}
          {showWalletAutoRefund && (
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
              <Wallet className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-800">Refund to BMF Coins</p>
                <p className="text-xs text-green-600 mt-1">
                  {formatCurrency(order.total)} will be credited back to your BMF Coins balance instantly after cancellation.
                </p>
              </div>
            </div>
          )}

          {/* ── Mixed payment notice (wallet portion always goes back to BMF Coins) ── */}
          {isMixed && isPaid && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
              <span className="font-semibold">Note:</span> The BMF Coins portion of your payment will always be refunded back to your BMF Coins. Choose below for the online payment portion.
            </div>
          )}

          {/* ── Refund choice (Razorpay or mixed) ── */}
          {showRefundChoice && (
            <div>
              <p className="text-sm font-semibold text-gray-800 mb-3">Choose Refund Method</p>
              <div className="space-y-3">

                {/* Option: original source */}
                <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  refundTo === 'source'
                    ? 'border-[#548B2E] bg-[#F1F8E9]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="refundTo"
                    value="source"
                    checked={refundTo === 'source'}
                    onChange={() => setRefundTo('source')}
                    className="mt-0.5 accent-[#548B2E] flex-shrink-0"
                  />
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Refund to Original Payment Source</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Credited to your bank / UPI / card within 5–7 business days
                    </p>
                    <p className="text-xs text-amber-600 mt-1 font-medium">
                      Subject to admin approval before processing
                    </p>
                  </div>
                </label>

                {/* Option: BMF Coins */}
                <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  refundTo === 'wallet'
                    ? 'border-[#548B2E] bg-[#F1F8E9]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="refundTo"
                    value="wallet"
                    checked={refundTo === 'wallet'}
                    onChange={() => setRefundTo('wallet')}
                    className="mt-0.5 accent-[#548B2E] flex-shrink-0"
                  />
                  <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Wallet className="w-5 h-5 text-[#548B2E]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Refund as BMF Coins</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Credited to your BMF Coins instantly — use on your next order
                    </p>
                  </div>
                </label>

              </div>
            </div>
          )}

          {/* ── COD / unpaid notice ── */}
          {(isCOD || !isPaid) && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                {isCOD
                  ? 'This is a Cash on Delivery order. No payment has been collected, so no refund is applicable.'
                  : 'No payment has been collected for this order. Cancellation is free.'}
              </p>
            </div>
          )}

          {/* Reason */}
          <div>
            <label htmlFor="cancel-reason" className="block text-sm font-semibold text-gray-800 mb-2">
              Reason for cancellation <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="cancel-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
              placeholder="e.g. Ordered by mistake, found a better price…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#548B2E]/40 focus:border-[#548B2E] resize-none disabled:opacity-50"
            />
          </div>

          <p className="text-xs text-gray-400 text-center">
            This action cannot be undone. Your order will be permanently cancelled.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Keep Order
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <XCircle className="w-4 h-4" />}
            {isLoading ? 'Cancelling…' : 'Confirm Cancellation'}
          </button>
        </div>

      </div>
    </div>
  );
}
